import {
  GetBackProcedimentiIdComunicazioniParams,
  GetFrontRichiesteIdComunicazioniParams,
  TipoComunicazioneDTO,
} from '@framework/api-client';
import React from 'react';

export interface ICommunicationsContext {
  /**
   * Valorizzato con l'id del procedimento selezionato, solamente
   * in caso di BackOffice
   */
  procedureId?: string;
  /**
   * Valorizzato con l'ID del procedimento padre (se siamo in un
   * endoprocedimento)
   */
  parentProcedureId: string | undefined | null;

  /**
   * Elenco dei tipi di comunicazioni ricercabili, inviabili
   * e ricevibili
   */
  searchableTypes?: TipoComunicazioneDTO[];
  sendableTypes?: TipoComunicazioneDTO[];
  receivableTypes?: TipoComunicazioneDTO[];

  /**
   * Ricerca delle comunicazioni
   */
  query?:
    | GetFrontRichiesteIdComunicazioniParams
    | GetBackProcedimentiIdComunicazioniParams;

  /**
   * Imposta i parametri di ricerca
   */
  setQuery?: (
    params:
      | GetFrontRichiesteIdComunicazioniParams
      | GetBackProcedimentiIdComunicazioniParams,
  ) => void;

  /**
   * Indica se la procedura è aperta
   */
  isProcedureOpen?: boolean | null;
}

export const CommunicationsContext =
  React.createContext<ICommunicationsContext>({
    parentProcedureId: undefined,
  });

/**
 * Context per gestire le informazioni comuni alle comunicazioni sia
 * sul FrontOffice che sul BackOffice.
 *
 * Al momento è utilizzato solamente per l'ufficio di comunicazione,
 * ma può essere esteso.
 */
export function CommunicationsContextProvider(
  props: React.PropsWithChildren<ICommunicationsContext>,
) {
  const {
    procedureId,
    parentProcedureId,
    query,
    setQuery,
    searchableTypes,
    receivableTypes,
    sendableTypes,
    isProcedureOpen,
  } = props;

  const contextValue = React.useMemo(
    () => ({
      procedureId,
      parentProcedureId,
      query,
      setQuery,
      searchableTypes,
      receivableTypes,
      sendableTypes,
      isProcedureOpen,
    }),
    [
      procedureId,
      parentProcedureId,
      query,
      setQuery,
      searchableTypes,
      sendableTypes,
      receivableTypes,
      isProcedureOpen,
    ],
  );

  return (
    <CommunicationsContext.Provider value={contextValue}>
      {props.children}
    </CommunicationsContext.Provider>
  );
}

/**
 * Hook per accedere al contesto delle comunicazioni
 */
export const useCommunicationsContext = () => {
  return React.useContext(CommunicationsContext);
};
